/**
 * marketRegimeService -- public market regime read.
 *
 * Hits GET /market/regime, which reads SPY's latest GEX + COT from
 * regime-state-prod. JWT-required; no entitlement gate. Used by the
 * "Should I trade right now?" card on Trade Signals + Stock Signals.
 *
 * Module-level 5-minute cache so multiple cards on a page (or quick tab
 * switches) don't hammer the endpoint -- GEX refreshes hourly upstream.
 */
import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export interface MacroFactor { regime?: string; [k: string]: any; }
export interface MacroBlock {
  oil?: MacroFactor;
  gold?: MacroFactor;
  bonds?: MacroFactor;
  rates?: MacroFactor;
  combined_regime?: string;
  combined_penalty?: number;
  as_of_date?: string;
}

export interface MarketRegime {
  symbol: string;
  gex_tag: string | null;
  cot_tag: string | null;
  gex_as_of: string | null;
  cot_as_of: string | null;
  cot_source?: string | null;  // 'SPY' or 'ES (futures proxy)' — tells the UI where COT came from
  // Macro state -- added 2026-06-23. Freshest snapshot across all symbols.
  // Fixes the cross-symbol Macro Backdrop inconsistency where each notches
  // generator was embedding its own (drifted) snapshot.
  macro?: MacroBlock | null;
  macro_as_of?: string | null;
  macro_source_symbol?: string | null;
  // Equity-specific macro (VIX + SPY, direction-aware). Stocks page reads
  // this instead of the 4-factor `macro` (USO/GLD/TLT/IEF) because
  // bond/commodity stress is not relevant for individual stock trading.
  // Written by macro-state-writer-prod since 2026-06-27.
  equity_macro?: MacroBlock | null;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
let cache: { at: number; payload: MarketRegime } | null = null;

export const marketRegimeService = {
  async get(token: string): Promise<MarketRegime> {
    if (cache && Date.now() - cache.at < CACHE_TTL_MS) {
      return cache.payload;
    }
    const resp = await axios.get<MarketRegime>(`${API_BASE_URL}/market/regime`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    cache = { at: Date.now(), payload: resp.data };
    return resp.data;
  },
};
